import { Exam, FavoriteExam } from '../../../interfaces/exam';
import { Icd } from '../../my-central/patientService';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import IncludedExams from './IncludedExams';
import FavoriteExams from './FavoriteExams';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { useEffect } from 'react';
import { useLoading } from '../../../helpers/useLoading';
import { Spinner } from '../../../components/UI-CHAKRA/Spinner';
import { DEFAULT_PRIMARY_COLOR } from '../usePrescription/helpers.mock';
import { OriginEnum } from '../../../interfaces/medicine';

interface PrescriptionExamContentProps {
  exams: Exam[];
  onUpdateGeneralDataExam(structuredIcd: Icd | null, observation: string): void;
  onDeleteExam(exam: Exam): void;
  onUpdateExam(payload: Exam): Promise<void>;
  enableTussCodeOnInterface: boolean;
  registerExamConfirmCallback?: (index: number, cb: () => Promise<void>) => void;
  needsLaterality?: boolean;
  handleSetLaterality: (value: boolean, lateralityFilled: boolean) => void;
  structuredCid: Icd | null
}

export const EXAM_INSTRUCTIONS_LIMIT = 512;

function translateExam(favoriteExam: FavoriteExam): Exam {
  const exam: Exam = {
    idEzOrder: favoriteExam.idEzOrder,
    sigtap: favoriteExam.sigtap,
    Nome: favoriteExam.name,
    CodigoTUSS: favoriteExam.codeTuss,
    LadoRequerido: favoriteExam.requiresSide,
    AnestesiaRequerida: favoriteExam.requiresAnesthesia,
    Label: favoriteExam.label,
    Instrucoes: '',
    Lateralidade: null,
    AplicarAnestesia: favoriteExam.requiresAnesthesia,
    categoria: favoriteExam.category,
    CID10Estruturado: [],
    idExterno: favoriteExam.externalId,
    referencia: favoriteExam.referencia,
    ReferenciaExterna: favoriteExam.externalReference,
    tipoFormulario: null,
    OrigemExame: null,
    examId: favoriteExam.examId,
    Origem: favoriteExam.examOrigin as OriginEnum | undefined,
    customExamIndex: favoriteExam.customExamIndex,
  };

  return exam;
}

export default function PrescriptionExamContent(props: PrescriptionExamContentProps) {
  const {
    prescription,
    addExamToPrescription,
    removeExamFromPrescription,
    addFavoriteExam,
    removeFavoriteExam,
    favoriteExams,
    fetchFavoriteExams,
  } = usePrescriptionStore();

  const [loading, runWithLoading] = useLoading();

  const onFavoriteExam = async (exam: Exam) => {
    if (!prescription?.Prescritor.idPrescritor || !exam.referencia) return;

    try {
      if (favoriteExams.some((favoriteExam) => favoriteExam.referencia === exam.referencia)) {
        await removeFavoriteExam(exam);
        return;
      }

      await addFavoriteExam(exam);
    } catch (error) { }
  };

  const onAddFavoriteExamToPrescription = async (favoriteExam: FavoriteExam) => {
    addExamToPrescription({ exam: translateExam(favoriteExam), searchExamLogId: null });
  };

  const onRemoveExamFromPrescription = async (exam: Exam) => {
    removeExamFromPrescription(exam);
  };

  useEffect(() => {
    runWithLoading(async () => {
      await fetchFavoriteExams();
    });
  }, [fetchFavoriteExams, runWithLoading]);

  return (
    <Flex gap={'30px'} w={'100%'} mt={'20px'} p={'0px 10px'} flexDir={{ base: 'column', md: 'row' }}>
      {loading ? (
        <Flex w={'100%'} h={'100%'} justifyContent={'center'} alignItems={'center'}>
          <Spinner color={DEFAULT_PRIMARY_COLOR} />
        </Flex>
      ) : (
        <FavoriteExams
          includedExams={props.exams}
          favoriteExams={favoriteExams}
          onAddFavoriteExamToPrescription={onAddFavoriteExamToPrescription}
          onRemoveExamFromPrescription={onRemoveExamFromPrescription}
        />
      )}
      <IncludedExams
        exams={props.exams}
        favoriteExams={favoriteExams}
        onUpdateGeneralDataExam={props.onUpdateGeneralDataExam}
        onDeleteExam={props.onDeleteExam}
        onUpdateExam={props.onUpdateExam}
        onFavoriteExam={onFavoriteExam}
        enableTussCodeOnInterface={props.enableTussCodeOnInterface}
        registerExamConfirmCallback={props.registerExamConfirmCallback}
        handleSetLaterality={props.handleSetLaterality}
        needsLaterality={props.needsLaterality}
        structuredCid={props.structuredCid}
      />
    </Flex>
  );
}
